import type React from 'react'
import type { ComponentProps } from 'react'
import type { IconProps } from '../icons/types'
import { cn } from '../lib/cva'
import { Button } from './Button'
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip'

interface Action {
	label: string
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
	variant?: ComponentProps<typeof Button>['variant']
	icon?: React.FC<IconProps>
	disabled?: boolean
	isLoading?: boolean
	isSubmit?: boolean
	tooltip?: string
}

interface ActionBarProps {
	leftActions?: Action[]
	rightActions?: Action[]
	className?: string
	isLoading?: boolean
	variant?: 'page' | 'modal'
}

export const ActionBar: React.FC<ActionBarProps> = ({
	leftActions = [],
	rightActions = [],
	className,
	variant = 'page',
}: ActionBarProps) => {
	const buildActionButton = ({ label, variant, onClick, icon, disabled, isLoading, isSubmit, tooltip }: Action) => {
		const button = (
			<Button
				key={label}
				variant={variant || 'secondary'}
				onClick={onClick}
				icon={icon}
				disabled={disabled}
				isLoading={isLoading}
				type={isSubmit ? 'submit' : 'button'}>
				{label}
			</Button>
		)

		if (tooltip) {
			return (
				<Tooltip key={label}>
					<TooltipTrigger asChild>
						<span className='inline-block'>{button}</span>
					</TooltipTrigger>
					<TooltipContent>{tooltip}</TooltipContent>
				</Tooltip>
			)
		}

		return button
	}

	return (
		<div
			data-action-bar='true'
			className={cn(
				'bg-neutral-0 z-10 shadow-md border-t border-neutral-100',
				variant === 'page' && 'fixed bottom-0 right-0 left-15',
				variant === 'modal' && 'relative bottom-0 left-0 right-0',
				className,
			)}>
			<div className='flex justify-between items-center px-6 py-4'>
				<div className='flex gap-2'>{leftActions.map(buildActionButton)}</div>
				<div className='flex gap-2'>{rightActions.map(buildActionButton)}</div>
			</div>
		</div>
	)
}
